import { Injectable } from '@angular/core';

export const UNTHREAD_EMBEDDED_CONFIG_WINDOW_KEY = '__UNTHREAD_EMBEDDED_CONFIG__';
const APP_CONFIG_SELECTOR = 'script[type="application/json"][data-selector="unthread-config"]';

export interface EmbeddedUiConfig {
  hideUnthreadTopNav?: boolean;
  hostPathPrefix?: string;
  navItems?: string[];
}

export interface UnthreadEmbeddedConfig {
  enabled?: boolean;
  host?: string;
  ui?: EmbeddedUiConfig;
}

export interface NormalizedUnthreadEmbeddedConfig {
  enabled: boolean;
  host?: string;
  ui: Required<Pick<EmbeddedUiConfig, 'hideUnthreadTopNav'>> &
    Pick<EmbeddedUiConfig, 'hostPathPrefix' | 'navItems'>;
}

declare global {
  interface Window {
    [UNTHREAD_EMBEDDED_CONFIG_WINDOW_KEY]?: UnthreadEmbeddedConfig;
  }
}

@Injectable({ providedIn: 'root' })
export class EmbeddedAppConfigService {
  private readonly config = getEmbeddedAppConfig();

  get isEnabled(): boolean {
    return this.config.enabled;
  }

  get shouldHideUnthreadTopNav(): boolean {
    return this.config.enabled && this.config.ui.hideUnthreadTopNav;
  }

  get hostPathPrefix(): string | undefined {
    return this.config.ui.hostPathPrefix;
  }

  getConfig(): NormalizedUnthreadEmbeddedConfig {
    return this.config;
  }

  applyDocumentState(): void {
    document.documentElement.toggleAttribute('data-unthread-embedded', this.config.enabled);
    document.body.classList.toggle('unthread-embedded-mode', this.config.enabled);

    if (this.config.host) {
      document.documentElement.setAttribute('data-unthread-embedded-host', this.config.host);
    } else {
      document.documentElement.removeAttribute('data-unthread-embedded-host');
    }
  }

}

export function getEmbeddedAppConfig(): NormalizedUnthreadEmbeddedConfig {
  const windowConfig = window[UNTHREAD_EMBEDDED_CONFIG_WINDOW_KEY];

  if (windowConfig) {
    return normalizeEmbeddedConfig(windowConfig);
  }

  const appConfig = readInlineAppConfig();

  return normalizeEmbeddedConfig(appConfig.embedded);
}

function readInlineAppConfig(): { embedded?: UnthreadEmbeddedConfig } {
  const inlineJsonElement = document.querySelector(APP_CONFIG_SELECTOR);

  if (!inlineJsonElement?.textContent) {
    return {};
  }

  return JSON.parse(inlineJsonElement.textContent) as { embedded?: UnthreadEmbeddedConfig };
}

function normalizeEmbeddedConfig(config?: UnthreadEmbeddedConfig): NormalizedUnthreadEmbeddedConfig {
  const enabled = config ? config.enabled !== false : false;

  return {
    enabled,
    host: cleanString(config?.host),
    ui: {
      hideUnthreadTopNav: enabled && config?.ui?.hideUnthreadTopNav !== false,
      hostPathPrefix: normalizePathPrefix(config?.ui?.hostPathPrefix),
      navItems: normalizeNavItems(config?.ui?.navItems),
    },
  };
}

function cleanString(value?: string): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();

  return trimmedValue || undefined;
}

function normalizePathPrefix(value?: string): string | undefined {
  const cleanedValue = cleanString(value);

  if (!cleanedValue) {
    return undefined;
  }

  return cleanedValue.startsWith('/') ? cleanedValue : `/${cleanedValue}`;
}

function normalizeNavItems(value?: string[]): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value.map(cleanString).filter((item): item is string => Boolean(item));
}
